import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-hook-inview";

import styles from "./C252_DynamicPie.module.scss";

export interface extendedPie {
  title: string;
  colour: string;
  categoryText1: string;
  categoryText2: string;
  categoryText3: string;
  categoryText4: string;
  categoryText5: string;
  categoryText6: string;
  categoryText7: string;
  categoryText8: string;
  categoryText9: string;
  categoryText10: string;
  categoryPercentage1: string;
  categoryPercentage2: string;
  categoryPercentage3: string;
  categoryPercentage4: string;
  categoryPercentage5: string;
  categoryPercentage6: string;
  categoryPercentage7: string;
  categoryPercentage8: string;
  categoryPercentage9: string;
  categoryPercentage10: string;
}

type CirclePosition = {
  percentage: { title: string; percentage: number };
  running: number;
  last: number;
};

export const DynamicPieExtended = (props: extendedPie) => {
  const { colour, title } = props;

  const [ref, isVisible] = useInView({
    threshold: 0,
    unobserveOnEnter: true,
  });
  const blurTimeout = useRef<number | undefined>(undefined);
  const graphicRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const percentageData = Array.from(Array(10).keys())
    .map((i) => {
      if (
        props[`categoryText${i + 1}`] &&
        props[`categoryPercentage${i + 1}`]
      ) {
        return {
          title: props[`categoryText${i + 1}`],
          percentage: props[`categoryPercentage${i + 1}`],
        };
      }

      return null;
    })
    .filter((item) => item);

  const [hoverClasses, setHoverClasses] = useState([""]);
  const [hoverPosition, setHoverPosition] = useState<{
    top: number | string;
    left: number | string;
  }>({ top: 0, left: 0 });
  const [hoverText, setHoverText] = useState({ title: "", percentage: "" });
  const [circlePositions, setCirclePositions] = useState<CirclePosition[]>([]);

  const angle = (cx, cy, ex, ey) => {
    const dy = ey - cy;
    const dx = ex - cx;
    let theta = Math.atan2(dy, dx); // range (-PI, PI]
    theta *= 180 / Math.PI; // rads to degs, range (-180, 180]
    if (theta < 0) {
      theta = 360 + theta; // range [0, 360)
    }
    theta = theta + 90;
    if (theta > 360) {
      theta = theta - 360;
    }
    return theta;
  };

  const getOffset = (evt) => {
    let x = 0,
      y = 0;
    const el = evt.targetl;
    const elBounds = el.getBoundingClientRect();

    x = evt.clientX - elBounds.left;
    y = evt.clientY - elBounds.top;

    return { x: x, y: y };
  };

  const blur = () => {
    blurTimeout.current = setTimeout(() => {
      setHoverClasses([""]);
    }, 500) as unknown as number;
  };

  const hover = (perc, current, last) => {
    clearTimeout(blurTimeout.current);
    const halfway = (current - last) / 2 + last,
      circle = graphicRef.current.querySelector("svg").getBoundingClientRect(),
      graphic = graphicRef.current.getBoundingClientRect(),
      widthDiff = graphic.width - circle.width,
      heightDiff = graphic.height - circle.height,
      r = circle.width / 2,
      coords = getPosOnCircle(0, -(r - 30), halfway * 3.6);
    let direction = "up";

    const left =
      "calc((" + r + "px + (" + widthDiff + "px / 2)) + " + coords.x + "px)";
    const top =
      "calc((" + r + "px + (" + heightDiff + "px / 2)) + " + coords.y + "px)";

    if (halfway > 87.5 || halfway < 12.5) {
      direction = "down";
    } else if (halfway > 52.5) {
      direction = "right";
    } else if (halfway < 37.5) {
      direction = "left";
    }

    setHoverClasses([styles.active, styles[direction]]);
    setHoverPosition({ top, left });
    setHoverText(perc);
  };

  const getPosOnCircle = (oX, oY, angle) => {
    const radius = Math.sqrt(oX * oX + oY * oY);
    const oTheta = Math.atan2(oX, oY);
    const nTheta = oTheta - (angle * Math.PI) / 180;
    return { x: radius * Math.sin(nTheta), y: radius * Math.cos(nTheta) };
  };

  useEffect(() => {
    if (graphicRef.current && listRef.current) {
      const circles = graphicRef.current.querySelectorAll(
        "svg path:not(.blank)"
      );
      const listItems = listRef.current.querySelectorAll("li i");

      percentageData?.forEach((_percentage, i) => {
        const a = i * Math.floor(10 / percentageData.length);
        const colour = window
          .getComputedStyle(circles[circles.length - 1 - a], null)
          .getPropertyValue("stroke");

        if (listItems[i]) {
          // @ts-expect-error type mismatch
          listItems[i].style.backgroundColor = colour;
        }
      });
    }
  }, [graphicRef, listRef]);

  useEffect(() => {
    if (isVisible && graphicRef.current) {
      let running = 0,
        last = 0;
      const circlePositions: CirclePosition[] = [];
      const circles = graphicRef.current.querySelectorAll(
        "svg path:not(.blank)"
      ) as NodeListOf<SVGPathElement>;

      percentageData?.forEach((percentage, i) => {
        running = running + parseFloat(percentage?.percentage);
        i = i * Math.floor(10 / percentageData.length);
        circles[circles.length - 1 - i].style.strokeDasharray =
          running - 0.25 + ",100";
        (
          circles[circles.length - 1 - i]
            .previousElementSibling as SVGPathElement
        ).style.strokeDasharray = running + ",100";

        circlePositions.push({
          percentage: percentage,
          running: running,
          last: last,
        });
        last = running;
      });
      setCirclePositions(circlePositions);
    }
  }, [isVisible, graphicRef]);

  // const dataPercent =

  return (
    <div
      ref={ref}
      className={clsx(
        styles.chart,
        styles[colour],
        isVisible && styles.animate
      )}
      data-percent="@Html.Raw(Model.DataPercent)"
    >
      <div ref={graphicRef} className={styles.graphic}>
        <div
          className={clsx(styles.hover, ...hoverClasses)}
          style={hoverPosition}
        >
          <div className={styles.inner}>
            {hoverText.title}
            <span>{hoverText.percentage}</span>
          </div>
        </div>
        <div className={styles.number}>
          <p>{title}</p>
        </div>
        <svg
          viewBox="-2 -2 40 40"
          className={styles["circular-chart"]}
          onMouseMove={(e) => {
            const node = e.target as HTMLElement;
            const test = getOffset(e),
              bounds = node.getBoundingClientRect(),
              angle2 = angle(
                bounds.width / 2,
                bounds.width / 2,
                test.x,
                test.y
              ),
              percentage = angle2 / 3.6;
            circlePositions.forEach((pos) => {
              if (pos.running >= percentage && pos.last < percentage) {
                hover(pos.percentage, pos.running, pos.last);
              }
            });
          }}
          onMouseLeave={() => {
            blur();
          }}
        >
          <path
            className={styles.circle}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={styles.circle}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.two)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.three)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.four)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.five)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.six)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.seven)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.eight)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.nine)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank")}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.ten)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
          <path
            className={clsx(styles.circle, styles.blank, "blank", styles.hov)}
            strokeDasharray="0, 100"
            d="M18 2.0845
	    a 15.9155 15.9155 0 0 1 0 31.831
	    a 15.9155 15.9155 0 0 1 0 -31.831"
          ></path>
        </svg>
      </div>
      <div className={styles.list} ref={listRef}>
        <ul>
          {percentageData?.map((percentage, i) => {
            return (
              <li key={`${percentage?.title}${i}`}>
                <i></i> {percentage?.title}{" "}
                <strong>{percentage?.percentage} %</strong>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default DynamicPieExtended;
